/**
 * @email kpozos@ibl.mx
 * @author Karen Yunuen Pozos Arias
 * @jobTitle Full Stack Developer
 *
 * Custom hook that manages the logic for updating a visitor type.
 * It handles:
 * - Fetching existing visitor type data by ID.
 * - Validating input and custom fields.
 * - Sending API requests to update the visitor type.
 * - Displaying feedback messages.
 * - Navigation after successful update.
 *
 * @param {function} t - Translation function.
 * @param {string|number} id - Visitor type ID to update.
 * @param {function} navigate - React Router navigate function.
 * @param {function} dispatch - Redux dispatch function.
 * @param {Object} item - Visitor type model data.
 * @param {function} setItem - Setter for visitor type model.
 * @param {Array} fields - List of custom fields.
 * @param {function} setFields - Setter for custom fields list.
 * @param {function} reset - Function to reset form state.
 * @param {function} setEmptyFields - Setter for empty field validation flag.
 * @param {function} setFieldData - Setter for custom field model.
 * @param {function} handleOnBack - Function to handle navigation after update.
 *
 * @returns {Object} Exposes the `handleUpdate` function to trigger the update process.
 */

import {
    GetVisitorTypeById,
    UpdateVisitorTypes
} from "../../../../../reduxToolkit/VisitorTypes/VisitorTypesApi";
import { useEffect } from "react";
import { toast } from "react-toastify";
import {errorMsg} from "../../../../../Apis/functions";
import {CUSTOM_FIELD_MODEL, VISITOR_TYPE_MODEL} from "../Model/Model";

const UpdateVisitorTypeApp = ({
    t,
    id,
    location,
    state,
    navigate,
    dispatch,
    item,
    setItem,
    fields,
    setFields,
    setEmptyFields,
    setFieldData
}) => {

    /**
     * Fetch visitor type data when the ID changes or component mounts.
     */
    useEffect(() => {
        if (id) getData();
    }, [id]);

    const getData = () => {
        dispatch(GetVisitorTypeById(id)).then(({ payload: { data: { data } } }) => {
            if (data?.id) {
                setItem(VISITOR_TYPE_MODEL.set(data));
                setFields(data.fields);
            }
        });
    }

    /**
     * Handles the update process for a visitor type.
     * Validates input, sends update request, and manages feedback and navigation.
     */
    const handleUpdate = () => {
        // Validate visitor type data
        if (!item.isValid()) {
            setEmptyFields(true);
            return;
        }
        // Validate almost one custom fields
        if(!item.isValidFields(fields)){
            setEmptyFields(true);
            toast.warning(t("add_at_least_one_custom_field"));
            return;
        }
        setEmptyFields(false);
        dispatch(UpdateVisitorTypes(item.toRegister(fields))).then(({ payload: { data: { data, success, code } } }) => {
            if (success) {
                // Reset custom field state
                setFieldData({ ...CUSTOM_FIELD_MODEL });

                navigate(location.pathname, { replace: true, state: {...state, id: data.id} });
                toast.success(t('has_been_updated_successfully'));
            } else {
                errorMsg(code);
            }
        });
    };

    return {
        handleUpdate
    };
};

export default UpdateVisitorTypeApp;
